/**
 * Product hero (catalog mode) — variant matching (colour/size swap image +
 * availability + computed box-coverage), SKU-based gallery filtering,
 * click-to-zoom, and sticky-on-scroll bar. Enquire is the shared
 * [data-enquire-trigger] popup (assets/enquiry-modal.js) — no inline form or
 * sample toggle on this template.
 */
(function () {
  'use strict';

  // Shopify's image_tag filter auto-generates a `srcset` on the main/sticky
  // <img> tags. Per the HTML spec, when `srcset` is present the browser
  // resolves the DISPLAYED image from srcset (ignoring plain `src` changes
  // made via JS) — so setting `.src` alone silently did nothing visually,
  // while `.src`/`variant`/sticky text/code all updated correctly. Clearing
  // `srcset` (and `sizes`) before setting `.src` is required every time.
  function setImageSrc(imgEl, src) {
    if (!imgEl || !src) return;
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('sizes');
    imgEl.src = src;
  }

  function initShare(root) {
    var btn = root.querySelector('[data-role="share"]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (navigator.share) {
        navigator.share({ title: document.title, url: window.location.href }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(window.location.href).catch(function () {});
      }
    });
  }

  function initGallery(root, variants, media) {
    var groups = root.querySelectorAll('[data-role="variant-group"]');
    var selected = {};

    groups.forEach(function (group) {
      var activeBtn = group.querySelector('.is-active') || group.querySelector('button');
      var optionIndex = group.getAttribute('data-option-index');
      selected[optionIndex] = activeBtn ? activeBtn.getAttribute('data-value') : null;
    });

    function findVariant(overrides) {
      var combo = Object.assign({}, selected, overrides || {});
      var optionIndexes = Object.keys(combo).sort();
      return variants.filter(function (variant) {
        return optionIndexes.every(function (idx) {
          return variant.options[Number(idx)] === combo[idx];
        });
      })[0];
    }

    function objectWith(key, value) {
      var obj = {};
      obj[key] = value;
      return obj;
    }

    function updateSizeSubtext() {
      groups.forEach(function (group) {
        if (!group.classList.contains('pmc__size-grid')) return;
        var optionIndex = group.getAttribute('data-option-index');
        group.querySelectorAll('.pmc__size-chip').forEach(function (chip) {
          var candidate = findVariant(objectWith(optionIndex, chip.getAttribute('data-value')));
          var subEl = chip.querySelector('[data-role="size-sub"]');
          if (subEl) {
            if (candidate && candidate.box_coverage) {
              subEl.textContent = candidate.box_coverage;
              subEl.hidden = false;
            } else {
              subEl.hidden = true;
            }
          }
        });
      });
    }

    function applyVariant(variant) {
      if (!variant) return;

      var stickyVariant = root.querySelector('[data-role="sticky-variant"]');
      var skuEl = root.querySelector('[data-role="product-code"]');
      var availRow = root.querySelector('[data-role="avail-row"]');
      var availText = root.querySelector('[data-role="avail-text"]');

      // The SKU-matched gallery photos are the single source of truth for
      // what's displayed (see filterGalleryBySku below) — variant.featured_image
      // is only a fallback for a variant with no SKU-tagged photos at all,
      // so the gallery thumbnails and the big image can never disagree with
      // each other the way they could when both were set independently.
      var matched = filterGalleryBySku(variant.sku);
      if (!matched && variant.featured_image) {
        setImageSrc(root.querySelector('[data-role="main-media"] img'), variant.featured_image);
        setImageSrc(root.querySelector('[data-role="sticky-image"] img'), variant.featured_image);
        if (variant.featured_media_id) {
          root.querySelectorAll('[data-role="thumb"]').forEach(function (thumb) {
            thumb.classList.toggle('is-active', thumb.getAttribute('data-media-id') === String(variant.featured_media_id));
          });
        }
      }

      if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title;
      if (skuEl && variant.sku) skuEl.textContent = variant.sku;

      if (availRow && availText) {
        var isAvailable = variant.available;
        availRow.toggleAttribute('data-order', !isAvailable);
      }

      updateSizeSubtext();
    }

    // Colour swatches only show ONE image per variant natively (Shopify has
    // no multi-image-per-variant relationship exposed to Liquid). This
    // store's tile images are uploaded with the variant SKU baked into the
    // filename (e.g. SKU "309020177" -> "309020177_V1.jpg", "..._V2.jpg"),
    // so that's used as the grouping key instead of relying on merchant-set
    // Alt text: every image whose filename contains the selected variant's
    // SKU is shown, and the rest are hidden. This is also what drives the
    // main + sticky image and the active thumbnail — NOT variant.featured_image
    // — because that's a separate single pointer the merchant sets in Admin
    // and it isn't guaranteed to match the actual SKU-tagged photos, which
    // was the cause of the main image sometimes showing a different colour
    // than the one just selected while the (correctly-filtered) thumbnail
    // rail showed the right photos. Returns true if any photos matched, so
    // callers can fall back to variant.featured_image when the SKU-naming
    // convention isn't followed for a given product.
    function filterGalleryBySku(sku) {
      var thumbs = root.querySelectorAll('[data-role="thumb"]');

      var visibleIds = sku
        ? media.filter(function (m) { return m.src && m.src.indexOf(sku) > -1; }).map(function (m) { return String(m.id); })
        : [];

      if (visibleIds.length === 0) {
        thumbs.forEach(function (t) { t.hidden = false; });
        return false;
      }

      thumbs.forEach(function (t) {
        t.hidden = visibleIds.indexOf(t.getAttribute('data-media-id')) === -1;
      });

      var firstVisibleId = visibleIds[0];
      var firstVisibleMedia = media.filter(function (m) { return String(m.id) === firstVisibleId; })[0];
      if (firstVisibleMedia) {
        setImageSrc(root.querySelector('[data-role="main-media"] img'), firstVisibleMedia.src);
        setImageSrc(root.querySelector('[data-role="sticky-image"] img'), firstVisibleMedia.src);
      }
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-media-id') === firstVisibleId);
      });

      return true;
    }

    groups.forEach(function (group) {
      var optionIndex = group.getAttribute('data-option-index');

      group.querySelectorAll('button[data-value]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selected[optionIndex] = btn.getAttribute('data-value');

          group.querySelectorAll('button[data-value]').forEach(function (b) {
            b.classList.toggle('is-active', b === btn);
          });

          var selectedLabel = root.querySelector('[data-role="option-selected-' + optionIndex + '"]');
          if (selectedLabel) selectedLabel.textContent = selected[optionIndex];

          applyVariant(findVariant());
        });
      });
    });

    // Manual thumbnail browsing (independent of variant selection). Reads
    // the thumbnail's own <img> src directly (bumped to a larger width)
    // rather than looking the media id up in the JSON blob, so a click
    // always shows something — it can't silently no-op on an id mismatch.
    root.querySelectorAll('[data-role="thumb"]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var thumbImg = thumb.querySelector('img');
        if (!thumbImg) return;

        var src = thumbImg.currentSrc || thumbImg.src;
        src = /[?&]width=\d+/.test(src) ? src.replace(/([?&])width=\d+/, '$1width=900') : src;
        setImageSrc(root.querySelector('[data-role="main-media"] img'), src);
        setImageSrc(root.querySelector('[data-role="sticky-image"] img'), src);

        root.querySelectorAll('[data-role="thumb"]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    });

    // Filter to the initially-selected variant's photos on page load too.
    var initialVariant = findVariant();
    if (initialVariant) filterGalleryBySku(initialVariant.sku);

    updateSizeSubtext();
  }

  // "Click to zoom" — opens whatever image is currently showing in a
  // full-screen overlay, requested at a larger width via the same Shopify
  // CDN image URL (bumping its `width` query param) rather than tracking a
  // separate high-res source, so it always matches whatever the gallery is
  // currently displaying (variant swap, thumbnail click, etc). Prev/Next
  // step through whichever thumbnails are currently visible (respecting
  // the SKU-based colour filter) by delegating to the thumbnail's own click
  // handler, so the gallery and active thumbnail stay in sync with
  // whatever's shown in the zoom view once it's closed.
  function initZoom(root) {
    var overlay = root.querySelector('[data-role="zoom-overlay"]');
    var zoomImg = overlay ? overlay.querySelector('[data-role="zoom-image"]') : null;
    var closeBtn = overlay ? overlay.querySelector('[data-role="zoom-close"]') : null;
    var prevBtn = overlay ? overlay.querySelector('[data-role="zoom-prev"]') : null;
    var nextBtn = overlay ? overlay.querySelector('[data-role="zoom-next"]') : null;
    if (!overlay || !zoomImg) return;

    var currentIndex = 0;

    function hiResUrl(url) {
      if (!url) return url;
      if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=1600');
      var sep = url.indexOf('?') > -1 ? '&' : '?';
      return url + sep + 'width=1600';
    }

    function visibleThumbs() {
      return Array.prototype.filter.call(root.querySelectorAll('[data-role="thumb"]'), function (t) {
        return !t.hidden;
      });
    }

    function syncArrows() {
      var hide = visibleThumbs().length <= 1;
      if (prevBtn) prevBtn.hidden = hide;
      if (nextBtn) nextBtn.hidden = hide;
    }

    function open() {
      var thumbs = visibleThumbs();
      var mainImg = root.querySelector('[data-role="main-media"] img');
      if (!mainImg) return;

      currentIndex = 0;
      thumbs.forEach(function (t, i) {
        if (t.classList.contains('is-active')) currentIndex = i;
      });

      zoomImg.src = hiResUrl(mainImg.currentSrc || mainImg.src);
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      syncArrows();
    }

    function close() {
      overlay.hidden = true;
      document.body.style.overflow = '';
    }

    function go(direction) {
      var thumbs = visibleThumbs();
      if (thumbs.length === 0) return;
      currentIndex = ((currentIndex + direction) % thumbs.length + thumbs.length) % thumbs.length;
      var thumb = thumbs[currentIndex];
      thumb.click(); // keeps the underlying gallery + main image in sync
      var thumbImg = thumb.querySelector('img');
      if (thumbImg) zoomImg.src = hiResUrl(thumbImg.currentSrc || thumbImg.src);
    }

    root.querySelectorAll('[data-role="zoom-trigger"]').forEach(function (btn) {
      btn.addEventListener('click', open);
    });

    var mainImgEl = root.querySelector('[data-role="main-media"] img');
    if (mainImgEl) mainImgEl.addEventListener('click', open);

    if (closeBtn) closeBtn.addEventListener('click', close);
    zoomImg.addEventListener('click', close);
    if (prevBtn) {
      prevBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        go(-1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        go(1);
      });
    }
    document.addEventListener('keydown', function (event) {
      if (overlay.hidden) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    });
  }

  function initStickyBar(root) {
    var stickyBar = root.querySelector('[data-pmc-sticky]');
    var enquireBox = root.querySelector('.pmc__enquire-box');
    if (!stickyBar || !enquireBox) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          stickyBar.classList.toggle('show', !entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { threshold: 0 }
    );
    observer.observe(enquireBox);
  }

  function init(root) {
    var dataScript = root.querySelector('[data-role="pmc-variant-data"]');
    var data = { variants: [], media: [] };

    if (dataScript) {
      try {
        data = JSON.parse(dataScript.textContent);
      } catch (e) {
        /* ignore */
      }
    }

    initShare(root);
    initGallery(root, data.variants || [], data.media || []);
    initZoom(root);
    initStickyBar(root);
  }

  document.querySelectorAll('.pmc').forEach(init);
})();
